import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LocationSearch } from "./GoogleMap/LocationSearch";
import { LocateFixed } from "lucide-react";
import { Tooltip } from "@mui/material";
import { useToast } from "@/hooks/use-toast";
// import { Tooltip } from "@mui/material";

interface HeroSectionProps {
  onSearch?: (filters: any) => void;
}

export default function HeroSection({ onSearch }: HeroSectionProps) {
  const { toast } = useToast();
  const [searchForm, setSearchForm] = useState({
    location: "",
    priceRange: "",
    propertyType: "",
    beds: "",
    baths: "",
    coordinates: {
      lat: 0,
      lng: 0
    },
    radius: 5000
  });

  const runSearch = (coordinates?: any) => {
    const filters: any = {};

    if (searchForm.location) {
      if (searchForm.location.split(",").length > 1) {
        filters.city = searchForm.location.split(",")[0];
        filters.state = searchForm.location.split(",")[1];
      }
      else {
        filters.search = searchForm.location;
      }

    }

    if (searchForm.priceRange && searchForm.priceRange !== "any") {
      const [min, max] = searchForm.priceRange.split('-').map(Number);
      if (min) filters.minPrice = min;
      if (max) filters.maxPrice = max;
    }

    if (searchForm.propertyType && searchForm.propertyType !== "any") {
      filters.propertyType = searchForm.propertyType;
    }

    if (searchForm.beds && searchForm.beds !== "any") {
      filters.beds = Number(searchForm.beds);
    }

    if (searchForm.baths && searchForm.baths !== "any") {
      filters.baths = Number(searchForm.baths);
    }
    if (coordinates) {
      filters.lat = coordinates.lat;
      filters.lng = coordinates.lng;
    }
    else if (searchForm.coordinates?.lat && searchForm.coordinates?.lng) {
      filters.lat = searchForm.coordinates.lat;
      filters.lng = searchForm.coordinates.lng;
    }
    filters.radius = searchForm.radius

    onSearch?.(filters);
  };

  const handleClearFilters = () => {
    setSearchForm({
      location: "",
      priceRange: "",
      propertyType: "",
      beds: "",
      baths: "",
      coordinates: {
        lat: 0,
        lng: 0
      },
      radius: 5000
    });
    onSearch?.({});
  };

  const handleSearch = (e: React.FormEvent | null) => {
    e?.preventDefault();
    runSearch();
  };

  const hasActiveFilters = Object.entries(searchForm).some(([key, val]) => {
    if (typeof val === "string") {
      return val !== "" && val !== "any";
    }
    if (typeof val === "object" && val !== null) {
      // specifically check coordinates
      if ("lat" in val && "lng" in val) {
        return val.lat !== 0 || val.lng !== 0;
      }
      return false;
    }
    if (typeof val === "number") {
      return key === "radius" ? val !== 5000 : val !== 0;
    }
    return false;
  });

  const handleLocate = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setSearchForm((prevForm) => ({
          ...prevForm,
          coordinates: { lat: latitude, lng: longitude },
        }));
        runSearch({ lat: latitude, lng: longitude });
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast({
              variant: "destructive",
              title: "Error!",
              description: "Location access denied. Please enable it in settings.",
            })
            break;
          case error.POSITION_UNAVAILABLE:
            toast({
              variant: "destructive",
              title: "Error!",
              description: "Location unavailable. Try moving to an open area.",
            })
            break;
          case error.TIMEOUT:
            toast({
              variant: "destructive",
              title: "Error!",
              description: "Location request timed out. Please try again.",
            })
            break;
          default:
            toast({
              variant: "destructive",
              title: "Error!",
              description: "Unable to fetch location. Please try again.",
            })
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };


  return (
    <section className="relative bg-gradient-to-r from-primary to-blue-700 text-white py-20">
      <div className="absolute inset-0 bg-black opacity-20"></div>
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&h=1080')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      ></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-4xl md:text-6xl font-bold mb-6">Find Your Dream Home</h1>
        <p className="text-xl mb-8 max-w-2xl mx-auto">
          Search thousands of properties for sale and rent across Ireland
        </p>

        {/* Search Form */}
        <div className="bg-white rounded-lg shadow-2xl p-6 max-w-4xl mx-auto">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            {/* Location Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Location</label>
              <div className="relative">
                <i className="fas fa-map-marker-alt absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                {/* <Input
                  type="text"
                  placeholder="County, City, Town or Area"
                  value={searchForm.location}
                  onChange={(e) => setSearchForm(prev => ({ ...prev, location: e.target.value }))}
                  className="pl-10 text-gray-900"
                  data-testid="input-hero-location"
                /> */}
                <LocationSearch value={searchForm.location} onChange={(v) => setSearchForm(prev => ({ ...prev, location: v }))} placeholder="County, City, Town or Area" />
              </div>
            </div>

            {/* Price Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Price Range</label>
              <Select value={searchForm.priceRange} onValueChange={(value) => setSearchForm(prev => ({ ...prev, priceRange: value }))}>
                <SelectTrigger className="text-gray-900" data-testid="select-hero-price">
                  <SelectValue placeholder="Any Price" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Price</SelectItem>
                  <SelectItem value="0-100000">₹0 - ₹100,000</SelectItem>
                  <SelectItem value="100000-200000">₹100,000 - ₹200,000</SelectItem>
                  <SelectItem value="200000-400000">₹200,000 - ₹400,000</SelectItem>
                  <SelectItem value="400000-600000">₹400,000 - ₹600,000</SelectItem>
                  <SelectItem value="600000-">₹600,000+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search Button */}
            <div>
              <Button type="submit" className="w-full" data-testid="button-hero-search">
                <i className="fas fa-search mr-2"></i>Search
              </Button>
            </div>
          </form>

          {/* Additional Filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200">
            <Select value={searchForm.propertyType} onValueChange={(value) => setSearchForm(prev => ({ ...prev, propertyType: value }))}>
              <SelectTrigger className="text-gray-900 text-sm" data-testid="select-hero-property-type">
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Property Type</SelectItem>
                <SelectItem value="apartment">Apartment</SelectItem>
                <SelectItem value="house">House</SelectItem>
                <SelectItem value="studio">Studio</SelectItem>
              </SelectContent>
            </Select>

            <Select value={searchForm.beds} onValueChange={(value) => setSearchForm(prev => ({ ...prev, beds: value }))}>
              <SelectTrigger className="text-gray-900 text-sm" data-testid="select-hero-bedrooms">
                <SelectValue placeholder="Bedrooms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Bedrooms</SelectItem>
                <SelectItem value="1">1+</SelectItem>
                <SelectItem value="2">2+</SelectItem>
                <SelectItem value="3">3+</SelectItem>
                <SelectItem value="4">4+</SelectItem>
              </SelectContent>
            </Select>

            <Select value={searchForm.baths} onValueChange={(value) => setSearchForm(prev => ({ ...prev, baths: value }))}>
              <SelectTrigger className="text-gray-900 text-sm" data-testid="select-hero-bathrooms">
                <SelectValue placeholder="Bathrooms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Bathrooms</SelectItem>
                <SelectItem value="1">1+</SelectItem>
                <SelectItem value="2">2+</SelectItem>
                <SelectItem value="3">3+</SelectItem>
              </SelectContent>
            </Select>

            {/* <button type="button" className="text-primary text-sm font-medium hover:text-blue-700" data-testid="button-more-filters">
              <i className="fas fa-sliders-h mr-1"></i>More Filters
            </button> */}
            {/* Clear Filter button */}
            <div className="flex gap-5">
              <Tooltip title="Search Near Me">
                <LocateFixed className="w-6 h-6 text-blue-500 cursor-pointer mt-2" onClick={handleLocate} />
              </Tooltip>
              <button
                type="button"
                className={`text-sm font-medium hover:text-red-700 disabled:opacity-50 ${hasActiveFilters ? "text-red-600" : "text-gray-400"
                  }`}
                data-testid="button-clear-filters"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
              >
                <i className="fas fa-times mr-1"></i>Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
